import {
  loginOutcomeSchema,
  loginResponseSchema,
  sessionResponseSchema,
  twoFactorChallengeSchema,
  type AuthenticatedUser,
  type LoginOutcome,
  type LoginRequest,
  type TwoFactorVerifyInput,
  type TwoFactorChallenge,
  type TwoFactorMethod,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { EncryptedQuestionnaireOfflineStore } from '@/features/questionnaires/lib/encrypted-offline-store';
import { apiFetch, apiSend, ApiError } from '@/lib/api-client';

export const sessionQueryKey = ['auth', 'session'] as const;

export function useSession(): UseQueryResult<AuthenticatedUser | null, ApiError> {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: async () => {
      try {
        const response = await apiFetch('/api/v1/auth/me', sessionResponseSchema);
        return response.user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin(): ReturnType<typeof useMutation<LoginOutcome, ApiError, LoginRequest>> {
  const queryClient = useQueryClient();

  return useMutation<LoginOutcome, ApiError, LoginRequest>({
    mutationFn: (input) =>
      apiFetch('/api/v1/auth/login', loginOutcomeSchema, {
        method: 'POST',
        body: input,
        skipRefresh: true,
      }),
    onSuccess: (outcome) => {
      if (!outcome.twoFactorRequired) {
        queryClient.setQueryData(sessionQueryKey, outcome.user);
      }
    },
  });
}

/** Zweiter Schritt der Anmeldung: TOTP-Code oder Backup-Code einlösen. */
export function useTwoFactorChallenge(): ReturnType<
  typeof useMutation<AuthenticatedUser, ApiError, TwoFactorVerifyInput>
> {
  const queryClient = useQueryClient();

  return useMutation<AuthenticatedUser, ApiError, TwoFactorVerifyInput>({
    mutationFn: async (input) => {
      const response = await apiFetch('/api/v1/auth/2fa/challenge', loginResponseSchema, {
        method: 'POST',
        body: input,
        skipRefresh: true,
      });
      return response.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
    },
  });
}

export function useSwitchTwoFactorMethod(): ReturnType<
  typeof useMutation<
    TwoFactorChallenge,
    ApiError,
    { challengeToken: string; method: TwoFactorMethod }
  >
> {
  return useMutation({
    mutationFn: (input) =>
      apiFetch('/api/v1/auth/2fa/challenge/method', twoFactorChallengeSchema, {
        method: 'POST',
        body: input,
        skipRefresh: true,
      }),
  });
}

export function useLogout(): ReturnType<typeof useMutation<void, ApiError, void>> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, void>({
    mutationFn: () => apiSend('/api/v1/auth/logout', { method: 'POST', skipRefresh: true }),
    onSuccess: async () => {
      const user = queryClient.getQueryData<AuthenticatedUser | null>(sessionQueryKey);
      try {
        if (user !== undefined && user !== null) {
          await EncryptedQuestionnaireOfflineStore.clearContext({
            tenantId: user.tenantId,
            userId: user.id,
          });
        }
      } finally {
        queryClient.setQueryData(sessionQueryKey, null);
        queryClient.clear();
      }
    },
  });
}
